import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

import theme from '../../../theme';
import { BLACK, PRIMARY_PURPLE } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import { cleanFullName } from '../helpers';

const styles = theme?.components?.userCard;

const EditableNameField = ({ initialValue, userId, disabled, isLoading, onNameSave }) => {
  const [name, setName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only update local state when:
    // 1. initialValue is defined (not undefined/null - distinguishes from loading state)
    // 2. Not currently processing an update (to avoid race conditions)
    // 3. Not currently editing (to avoid overwriting user input)
    // 4. The value has actually changed (to avoid unnecessary updates)
    if (
      initialValue !== undefined &&
      initialValue !== null &&
      !isLoading &&
      !isEditing &&
      name !== initialValue
    ) {
      setName(initialValue);
    }
  }, [initialValue, isLoading, isEditing, name]);

  const handleNameChange = (e) => {
    const { value } = e.target;
    setName(value);
    setIsEditing(true);
    setError('');
  };

  const handleNameSave = useCallback(() => {
    // Clean the name first to ensure consistency between validation and comparison
    const cleanedName = cleanFullName(name);
    // Clean initialValue to ensure consistent comparison
    const cleanedInitialValue = cleanFullName(initialValue || '');

    // Validate the cleaned name to ensure consistency
    const validationError = !/^[a-z ,.'-]+$/i.test(cleanedName)
      ? 'userCard.name.invalid.error'
      : undefined;
    setError(validationError);

    if (!validationError) {
      // Only update if the cleaned name has actually changed from the cleaned initial value
      if (cleanedName !== cleanedInitialValue) {
        onNameSave({ userId, name: cleanedName });
      }
      setIsEditing(false);
    }
  }, [name, initialValue, userId, onNameSave]);

  return (
    <Box width="100%" display="flex" flexDirection="column" gap={1}>
      <Typography sx={styles.workerTitle}>
        {LanguageConverterV2('userCard.subTitle.worker')}
      </Typography>
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {isEditing ? (
          <TextField
            disabled={disabled}
            value={name}
            onChange={handleNameChange}
            placeholder={LanguageConverterV2('userCard.name.placeholder')}
            variant="outlined"
            inputProps={{ 'data-testid': 'name-input' }}
            sx={styles.phoneInput}
          />
        ) : (
          <Typography sx={{ fontSize: '20px', fontFamily: PRIMARY_FONT[700], color: BLACK[80] }}>
            {name}
          </Typography>
        )}

        {isEditing ? (
          <Button
            variant="outlined"
            onClick={handleNameSave}
            data-testid="name-save-button"
            disabled={disabled}
            sx={{
              color: isEditing ? PRIMARY_PURPLE[70] : BLACK[60],
              fontFamily: PRIMARY_FONT[500],
              borderColor: isEditing ? PRIMARY_PURPLE[70] : BLACK[60],
              height: '40px',
            }}
          >
            {LanguageConverterV2('buttonText.save')}
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={() => setIsEditing(true)}
            data-testid="name-edit-button"
            disabled={disabled || isLoading}
            sx={{
              fontFamily: PRIMARY_FONT[500],
              height: '40px',
            }}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {LanguageConverterV2('button.edit')}
          </Button>
        )}
      </Box>
      {error && (
        <Typography data-testid="name-error-message" sx={styles.errorMessage}>
          {LanguageConverterV2(error)}
        </Typography>
      )}
    </Box>
  );
};

EditableNameField.propTypes = {
  initialValue: PropTypes.string,
  userId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  onNameSave: PropTypes.func.isRequired,
};

export default EditableNameField;
